<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import type { Rules } from "async-validator";
import { ref, type Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { getArrayDelta } from "~/core/utils/array";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useCreateUserGroup } from "~/modules/user/composables/useCreateUserGroup";
import { useGetUserGroup } from "~/modules/user/composables/useGetUserGroup";
import { useUpdateUserGroup } from "~/modules/user/composables/useUpdateUserGroup";
import type { GroupsUserDetail } from "~/modules/user/models/GroupUserDetail.interface";
import type { GroupUserDto } from "~/modules/user/models/GroupUserDto.interface";
import { UserColumns } from "~/modules/user/models/UserColumns.enum";
import type { ArrayDelta } from "~/types/arrayDelta";

const serialize = useSerialize();
const { t } = useI18n();
const { can } = useAcl();

const props = defineProps<{
  show: boolean;
  organizationCode?: number;
  groupName?: string;
}>();

const {
  getUserGroup,
  error: errorGetUserGroup,
  isLoading: isLoadingGetUserGroup,
  result: resultGetUserGroup,
} = useGetUserGroup({
  organizationCode: props.organizationCode,
});
const {
  updateUserGroup,
  error: errorUpdateUserGroup,
  isLoading: isLoadingUpdateUserGroup,
} = useUpdateUserGroup({
  organizationCode: props.organizationCode,
});
const {
  createUserGroup,
  error: errorCreateUserGroup,
  isLoading: isLoadingCreateUserGroup,
} = useCreateUserGroup({
  organizationCode: props.organizationCode,
});

const showCanvas: WritableComputedRef<boolean> = computed({
  get: () => {
    return props.show;
  },
  set: (shown: boolean) => {
    emit("update:show", shown);
  },
});
const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();

const emit = defineEmits(["update:show", "close"]);
const edited: Ref<boolean> = ref(false);
const groupFormRef: Ref<HTMLFormElement | undefined | null> = ref();
const userListColumns: UserColumns[] = [
  UserColumns.LAST_NAME,
  UserColumns.FIRST_NAME,
  UserColumns.PROFILE,
  UserColumns.GROUPS,
];

const groupForm: Partial<GroupsUserDetail> = reactive({
  name: "",
  sectorCodes: [],
  users: [],
});
let initFormStringified: Ref<string> = ref(serialize(groupForm));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(groupForm) !== initFormStringified.value,
);
const showSectors: ComputedRef<boolean> = computed(() => {
  return can(ACLRoles.CAN_READ_SECTOR);
});
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => groupFormRef.value?.isValid,
);
const formRules: Rules = {
  name: [
    {
      required: true,
      message: t("input.error.required"),
    },
  ],
};

async function onSubmit() {
  if (isFormValid.value) {
    const groupRequest: GroupUserDto = {
      name: groupForm.name!,
      sectorCodes: groupForm.sectorCodes || [],
    };
    if (props.groupName) {
      let initialUsers: string[] = resultGetUserGroup.value?.users || [];
      let finalUsers: string[] = groupForm.users || [];
      const arrayDelta: ArrayDelta = getArrayDelta(initialUsers, finalUsers);
      groupRequest.assign = arrayDelta.added as string[];
      groupRequest.unassign = arrayDelta.deleted as string[];
      await updateUserGroup(props.groupName, groupRequest);
      if (LxcError.check(errorUpdateUserGroup.value)) {
        errorUpdateUserGroup.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    } else {
      groupRequest.assign = groupForm.users || [];
      await createUserGroup(groupRequest);
      if (LxcError.check(errorCreateUserGroup.value)) {
        errorCreateUserGroup.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    }
  }
}

function onSuccess() {
  showNotificationSuccess(t(NotificationKey.saveSuccess));
  edited.value = true;
  initFormStringified.value = serialize(groupForm);
  setTimeout(close, 0); // use setTimeout in order to compute formHasChanged before closing
}

function onHidden() {
  clearForm();
  emit("close", {
    edited: edited.value,
  });
  edited.value = false;
}

function clearForm() {
  groupForm.name = "";
  groupForm.users = [];
  groupForm.sectorCodes = [];
  initFormStringified.value = serialize(groupForm);
  setTimeout(() => {
    groupFormRef.value?.clearValidate();
  }, 0);
}

async function reset() {
  if (props.groupName && props.show) {
    await getUserGroup(props.groupName);

    if (!errorGetUserGroup.value) {
      groupForm.name = props.groupName;
      groupForm.sectorCodes = resultGetUserGroup.value?.sectorCodes ?? [];
      groupForm.users = resultGetUserGroup.value?.users ?? [];
      initFormStringified.value = serialize(groupForm);
    }
  } else {
    errorGetUserGroup.value = null;
  }
}

const close = () => {
  sideCanvasRef.value?.hide();
};

watch(() => [props.groupName, props.show] as const, reset);

onMounted(reset);
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasRef"
    v-model:show="showCanvas"
    :header="
      groupName ? t('userGroup.update.title') : t('userGroup.create.title')
    "
    size="1/2"
    :confirm-title="
      groupName
        ? t('userGroup.update.confirm.title')
        : t('userGroup.create.confirm.title')
    "
    :confirm-message="t('userGroup.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    @hidden="onHidden"
  >
    <container-component
      :py="0"
      :px="0"
      :is-loading="isLoadingGetUserGroup"
      :error="errorGetUserGroup"
    >
      <lxc-form
        ref="groupFormRef"
        :model="groupForm"
        :rules="formRules"
        @submit.prevent="onSubmit"
      >
        <h6 class="mb-4 text-gray-700 font-bold">
          {{ t("userGroup.information") }}
        </h6>
        <lxc-form-item :label="t('userGroup.name')" prop="name">
          <lxc-input v-model="groupForm.name" type="text" />
        </lxc-form-item>
        <h6 class="mb-4 mt-2 text-gray-700 font-bold">
          {{ t("userGroup.sector.title") }}
        </h6>
        <lxc-form-item
          v-if="showSectors"
          :label="t('userGroup.sectors.label')"
          label-class="pb-2 inline-block text-sm"
          prop="sectors"
          class="pt-2 col-start-1"
          display-label
        >
          <user-sector-selection v-model="groupForm.sectorCodes" />
        </lxc-form-item>
        <h6 class="mb-4 mt-6 text-gray-700 font-bold flex justify-between">
          <span>{{ t("userGroup.addUsers.title") }}</span>
          <span class="font-normal text-sm">{{ t("input.optional") }}</span>
        </h6>
        <user-list
          v-if="show"
          v-model:selected-users="groupForm.users"
          disable-clickable
          show-quick-filter-selected-users
          :columns="userListColumns"
          :organization-code="organizationCode"
          :organization-type="OrganizationType.OPERATOR"
          :path-context="''"
          :show-user-form="false"
          :use-query-parameters-for-pagination="false"
        />
      </lxc-form>
    </container-component>

    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="close"
        />
        <lxc-button
          html-type="submit"
          type="primary"
          class="grow"
          :text="t('button.save')"
          :title="t('button.save')"
          :disabled="!isFormValid || !formHasChanged"
          :is-loading="isLoadingCreateUserGroup || isLoadingUpdateUserGroup"
          @click="onSubmit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
